import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("en-GB"); } catch { return ""; }
}

export default function AdminContact() {
  const { headers } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contact", { headers });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id, read) => {
    try {
      await api.patch(`/admin/contact/${id}`, { read }, { headers });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/contact/${id}`, { headers });
      toast.success("Deleted.");
      setOpen(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-contact">
      <div className="mb-10">
        <p className="overline text-graphite/55">Enquiries</p>
        <h1 className="mt-3 h-display text-4xl md:text-5xl">Contact submissions</h1>
        <p className="mt-4 text-graphite/65">{items.length} total · {items.filter((i) => !i.read).length} unread</p>
      </div>

      <div className="hairline-top">
        {items.map((c) => (
          <div key={c.id} className="grid grid-cols-12 gap-4 py-5 hairline-bottom items-center" data-testid={`admin-contact-${c.id}`}>
            <div className="col-span-1">
              <span className={`inline-block w-2 h-2 ${c.read ? "bg-graphite/25" : "bg-[#1E3668]"}`} title={c.read ? "Read" : "Unread"} />
            </div>
            <div className="col-span-3"><p className="font-semibold">{c.name}</p><p className="text-sm text-graphite/60">{c.email}</p></div>
            <div className="col-span-4 text-sm truncate">{c.subject || c.message.slice(0, 80)}</div>
            <div className="col-span-2 text-xs text-graphite/55">{formatDate(c.created_at)}</div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => { setOpen(c); if (!c.read) markRead(c.id, true); }} className="cec-btn !py-2 !px-4 text-xs" data-testid={`admin-view-${c.id}`}>Open</button>
              <button onClick={() => del(c.id)} className="p-2 hover:text-red-700" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="py-20 text-center text-graphite/55">No enquiries yet.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-graphite/40 flex items-center justify-center px-4" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-warmwhite max-w-2xl w-full p-8 md:p-12 hairline">
            <p className="overline text-graphite/55">From</p>
            <h2 className="mt-2 h-display text-3xl">{open.name}</h2>
            <p className="mt-1 text-graphite/65"><a href={`mailto:${open.email}`} className="hover:text-[#1E3668]">{open.email}</a>{open.company && <> · {open.company}</>}</p>
            <hr className="my-6 border-graphite/15" />
            {open.subject && <p className="overline text-graphite/55 mb-2">Subject</p>}
            {open.subject && <p className="font-medium text-lg mb-6">{open.subject}</p>}
            {open.expertise && <p className="text-sm text-graphite/65 mb-4">Practice: {open.expertise}</p>}
            <p className="overline text-graphite/55 mb-2">Message</p>
            <p className="whitespace-pre-line leading-relaxed text-graphite/85">{open.message}</p>
            <p className="mt-8 text-xs text-graphite/50">{formatDate(open.created_at)}</p>
            <div className="mt-8 flex items-center gap-3">
              <a href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject || "Your enquiry")}`} className="cec-btn cec-btn-primary">Reply by email</a>
              <button onClick={() => markRead(open.id, !open.read)} className="cec-btn"><Check size={14} /> Mark {open.read ? "unread" : "read"}</button>
              <button onClick={() => setOpen(null)} className="cec-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
